import type { AxisOptionGroup } from 'components/molecules/PackArchitecture/AxisConfigDropdown';

import { AXIS_PREVIEW_SALES_KPI_IDS, type SalesKpiIds } from './packArchitectureMeta.helpers';

export const normalizePackArchitectureKpiId = (value?: string) => value?.trim().toLowerCase() ?? '';

export const stripPackArchitectureKpiPrefix = (kpiId: string) =>
    normalizePackArchitectureKpiId(kpiId).replace(/^ps_/, '');

export type PackArchitectureKpiAliasLookup = Map<string, string[]>;

export type PackArchitectureKpiAliasConfig = {
    configuredKpiIds: string[];
    salesKpiIds?: SalesKpiIds;
};

export type PackArchitectureKpiIdMatcher = {
    aliasLookup: PackArchitectureKpiAliasLookup;
    matches: (cellKpiId: string, requestedKpiId: string) => boolean;
};

const idsMatchDirectly = (entryId: string, target: string) =>
    entryId === target ||
    entryId.endsWith(`_${target}`) ||
    target.endsWith(`_${entryId}`) ||
    stripPackArchitectureKpiPrefix(entryId) === stripPackArchitectureKpiPrefix(target);

const idsMatchSalesSubtype = (entryId: string, target: string) =>
    (target.includes('value_sales') && entryId.includes('value_sales')) ||
    (target.includes('volume_sales') && entryId.includes('volume_sales')) ||
    (target.includes('unit_sales') && entryId.includes('unit_sales'));

const buildSalesAnchorMatcher = (anchorId: string) => {
    const anchor = normalizePackArchitectureKpiId(anchorId);
    if (!anchor) return () => false;

    const salesToken = anchor.includes('unit')
        ? 'unit_sales'
        : anchor.includes('volume')
        ? 'volume_sales'
        : 'value_sales';

    return (entryId: string) =>
        idsMatchDirectly(entryId, anchor) ||
        idsMatchSalesSubtype(entryId, anchor) ||
        entryId === salesToken ||
        entryId.endsWith(`_${salesToken}`);
};

export const packArchitectureKpiIdsMatch = (
    cellKpiId: string,
    requestedKpiId: string,
    aliasLookup: PackArchitectureKpiAliasLookup,
    salesKpiIds: SalesKpiIds,
): boolean => {
    const entry = normalizePackArchitectureKpiId(cellKpiId);
    const target = normalizePackArchitectureKpiId(requestedKpiId);
    if (!entry || !target) return false;

    const candidates = aliasLookup.get(target);
    if (candidates?.some((candidate) => normalizePackArchitectureKpiId(candidate) === entry)) {
        return true;
    }

    if (idsMatchDirectly(entry, target) || idsMatchSalesSubtype(entry, target)) {
        return true;
    }

    const valueSalesMatcher = buildSalesAnchorMatcher(salesKpiIds.valueSales);
    const volumeSalesMatcher = buildSalesAnchorMatcher(salesKpiIds.volumeSales);
    const unitSalesMatcher = buildSalesAnchorMatcher(salesKpiIds.unitSales);

    return (
        (valueSalesMatcher(entry) && valueSalesMatcher(target)) ||
        (volumeSalesMatcher(entry) && volumeSalesMatcher(target)) ||
        (unitSalesMatcher(entry) && unitSalesMatcher(target))
    );
};

export const buildPackArchitectureKpiAliasLookup = ({
    configuredKpiIds,
    salesKpiIds = AXIS_PREVIEW_SALES_KPI_IDS,
}: PackArchitectureKpiAliasConfig): PackArchitectureKpiAliasLookup => {
    const knownIds = [...new Set(configuredKpiIds.filter(Boolean))];
    const lookup: PackArchitectureKpiAliasLookup = new Map();
    const valueSalesMatcher = buildSalesAnchorMatcher(salesKpiIds.valueSales);
    const volumeSalesMatcher = buildSalesAnchorMatcher(salesKpiIds.volumeSales);
    const unitSalesMatcher = buildSalesAnchorMatcher(salesKpiIds.unitSales);

    const resolveAliases = (requestedId: string): string[] => {
        const target = normalizePackArchitectureKpiId(requestedId);
        if (!target) return [];

        const aliases = new Set<string>([requestedId, target]);

        for (const configuredId of knownIds) {
            const normalized = normalizePackArchitectureKpiId(configuredId);
            if (!normalized) continue;

            const inSameConfiguredGroup =
                idsMatchDirectly(normalized, target) ||
                idsMatchSalesSubtype(normalized, target) ||
                (valueSalesMatcher(normalized) && valueSalesMatcher(target)) ||
                (volumeSalesMatcher(normalized) && volumeSalesMatcher(target)) ||
                (unitSalesMatcher(normalized) && unitSalesMatcher(target));

            if (inSameConfiguredGroup) {
                aliases.add(configuredId);
                aliases.add(normalized);
            }
        }

        return [...aliases];
    };

    for (const kpiId of knownIds) {
        const normalized = normalizePackArchitectureKpiId(kpiId);
        if (!normalized) continue;
        lookup.set(normalized, resolveAliases(kpiId));
    }

    return lookup;
};

export const collectConfiguredPackArchitectureKpiIds = (
    yAxisOptionGroups: AxisOptionGroup[],
    tableKpiOptionItems: Array<{ id?: string | null }>,
    salesKpiIds: SalesKpiIds = AXIS_PREVIEW_SALES_KPI_IDS,
    /** Bubble Size axis options (e.g. Value/Volume/Unit Sales) — included so any Size
     * KPI added in config is matchable without further frontend changes. */
    bubbleSizeOptionGroups: AxisOptionGroup[] = [],
): string[] => [
    ...new Set([
        ...yAxisOptionGroups.flatMap((group) => group.options.map((option) => option.value)),
        ...tableKpiOptionItems.map((item) => item.id ?? '').filter(Boolean),
        ...bubbleSizeOptionGroups.flatMap((group) => group.options.map((option) => option.value)),
        salesKpiIds.valueSales,
        salesKpiIds.volumeSales,
        salesKpiIds.unitSales,
    ]),
];

export const createPackArchitectureKpiIdMatcher = (
    config: PackArchitectureKpiAliasConfig,
): PackArchitectureKpiIdMatcher => {
    const salesKpiIds = config.salesKpiIds ?? AXIS_PREVIEW_SALES_KPI_IDS;
    const aliasLookup = buildPackArchitectureKpiAliasLookup({ ...config, salesKpiIds });

    return {
        aliasLookup,
        matches: (cellKpiId, requestedKpiId) =>
            packArchitectureKpiIdsMatch(cellKpiId, requestedKpiId, aliasLookup, salesKpiIds),
    };
};
